import React, { useState, useMemo, useCallback, useContext } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import { createTitle as createAlertTitle } from '#component/project/form/alerts/alert-item/helper';
import { ErrorsContext } from '#component/warehouse/appliance-form/form/errors-context';
import DevicePopup from '#component/device/devices-popup/device-popup';
import { AddDevicesButton, DeleteDevicesButton } from '#component/device/devices-popup/components/action-buttons/appliance';
import {
  useDevicePopupChangesAppliance,
  useFloatDevicesSyncAppliance
} from '#component/device/devices-popup/hooks';
import {
  setFilter,
  setDeviceType,
  changePage,
  setFormData,
  changeOrderAndOrderBy,
  fetchApplianceGroupDevices
} from '#action/warehouse/appliance/appliance-devices-popup';
import DeviceField from '#component/general/form-device-field';
import { getAllApplianceDevices } from '#component/warehouse/appliance-form/form/helper';
import DeviceItemActions from './device-item-actions';
import {
  StyledAccordion,
  StyledAccordionSummary,
  TitleContainer,
  Title,
  StyledAvatar,
  StyledAccordionDetails
} from './styles';

/**
 * Renders MUI expansion panel with device item fields.
 * Contains button to remove device-item.
 * @returns {React.FC}
 */

const DeviceItem = props => {
  const {
    index,
    member,
    remove,
    change,
    expanded,
    handleSetExpanded,
    numberOfItems,
    item,
    forceError,
    projectAlerts,
    projectId,
    applianceId,
    isApplianceStop
  } = props;
  const measurementSystem = useSelector(s => s.account?.measurementSystem, shallowEqual);

  const { control, watch, formState: { errors: formErrors } } = useFormContext();
  const deviceGroups = watch('devicesGroup');
  const selectedDevices = watch(`${member}.devices`) ?? [];
  const applianceDevices = getAllApplianceDevices(deviceGroups);

  const { errors, setErrors } = useContext(ErrorsContext);

  const alertId = deviceGroups?.[index]?.projectAlertId;

  const project = useMemo(() => ({ id: projectId }), [projectId]);

  const [title, setTitle] = useState(item.groupName);

  const onGroupNameChange = useCallback(e => setTitle(e.target.value), []);

  const deleteDeviceItem = event => {
    event.stopPropagation();
    remove(index);
  };

  const handleDeviceSetData = useCallback(d => {
    change(`${member}.devices`, d);
    if (errors?.devicesGroup?.[index]) {
      const untouchedErrors = [...errors.devicesGroup];
      untouchedErrors[index] = false;
      setErrors(s => ({ ...s, devicesGroup: untouchedErrors }));
    }
  }, [change, member, errors, index, setErrors]);

  const handleAlertSettingsBlur = (event, newValue, previousValue, name) => {
    event.preventDefault();
    change(name, newValue || null);
  };

  const onAlertSettingsChange = (event, newValue) => {
    handleDeviceSetData(selectedDevices.map(d => ({
      ...d,
      alertOn: !!newValue,
      showAlertIcon: !!newValue
    })));
  };

  const handlePanelClick = () => {
    if (errors.devicesGroup[index]) {
      const untouchedErrors = [...errors.devicesGroup];
      untouchedErrors[index] = false;
      setErrors(s => ({ ...s, devicesGroup: untouchedErrors }));
    }
  };

  const projectData = useMemo(
    () => ({
      id: projectId,
      title
    }),
    [projectId, title]
  );

  const applianceData = useMemo(
    () => ({
      applianceId,
      alertId,
      formName: 'DevicesApplianceForm',
      formDevicesField: 'devicesGroup',
      groupData: item,
      projectData,
      formIndex: index,
      projectId,
      formDevices: selectedDevices
    }),
    [applianceId, alertId, item, projectData, index, projectId, selectedDevices]
  );

  const devicePopupActions = useMemo(() => ({
    setFilter,
    setDeviceType,
    changePage,
    setFormData,
    changeOrderAndOrderBy,
    fetchDevices: fetchApplianceGroupDevices
  }), []);

  const devicePopupHooks = useMemo(() => ({
    usePopupChanges: useDevicePopupChangesAppliance,
    useDevicesSync: useFloatDevicesSyncAppliance
  }), []);

  const devicePopupData = useMemo(() => ({
    project,
    openedFromCreateForm: true,
    member,
    formIndex: index,
    data: applianceData,
    customComponents: { AddDevicesButton, DeleteDevicesButton },
    customActions: devicePopupActions,
    customHooks: devicePopupHooks,
    alertId,
    setFormDevices: handleDeviceSetData,
    formDevices: selectedDevices
  }), [
    project,
    member,
    index,
    applianceData,
    devicePopupActions,
    devicePopupHooks,
    alertId,
    handleDeviceSetData,
    selectedDevices
  ]);

  const alertOptions = useMemo(() => projectAlerts
    .filter(alert => !alert.archived || alert.id === alertId)
    .map(alert => (
      <MenuItem key={alert.id} value={alert.id}>
        {createAlertTitle(alert, 'metric', measurementSystem)}
      </MenuItem>
    )), [projectAlerts, measurementSystem, alertId]);

  const hasGroupError = !!formErrors?.devicesGroup?.[index] &&
    (forceError || !!errors?.devicesGroup?.[index]);

  return (
    <StyledAccordion
      expanded={expanded}
      onChange={handleSetExpanded(member)}
      hasError={hasGroupError}
      onClick={handlePanelClick}
    >
      <StyledAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index + 1}a-content`}
        id={`panel${index + 1}a-header`}
      >
        <TitleContainer>
          <StyledAvatar>{index + 1}</StyledAvatar>
          <Title>{title}</Title>
        </TitleContainer>
        {(index > 0 || numberOfItems > 1) &&
        <DeviceItemActions deleteDeviceItem={deleteDeviceItem} />}
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <RHFTextField
          name={`${member}.groupName`}
          label="Device group name"
          variant="outlined"
          fullWidth
          margin="normal"
          onChange={onGroupNameChange}
          forceError={forceError}
          disabled={isApplianceStop}
        />
        <RHFTextField
          name={`${member}.projectAlertId`}
          label="Alert settings"
          variant="outlined"
          fullWidth
          margin="normal"
          onChange={onAlertSettingsChange}
          onBlur={handleAlertSettingsBlur}
          select
          disabled={isApplianceStop}
        >
          <MenuItem value={null}>
            None
          </MenuItem>
          {alertOptions}
        </RHFTextField>
        <Controller
          name={`${member}.devices`}
          control={control}
          render={() => (
            <DeviceField
              name={`${member}.devices`}
              id={`device-cargo-${index}filter-multiple-select`}
              label="Devices"
              placeholder="Devices"
              project={project}
              openedFromCreateForm={true}
              formData={selectedDevices}
              allFormDevices={applianceDevices}
              handleDeviceSetData={handleDeviceSetData}
              devicePopupData={devicePopupData}
              DevicePopupComponent={DevicePopup}
              isApplianceDeviceAlign={true}
              isApplianceStop={isApplianceStop}
              forceError={forceError}
              alertId={alertId}
              meta={{
                error: formErrors?.devicesGroup?.[index]?.devices?.message,
                invalid: !!formErrors?.devicesGroup?.[index]?.devices?.message,
                submitFailed: forceError
              }}
            />
          )}
        />
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default DeviceItem;
